import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, ShieldAlert, X } from "lucide-react";

/**
 * /admin/availability — month-view calendar where admin can block whole dates
 * or AM·PM slots. Same Swiss editorial styling. Mobile-first.
 */
export default function AdminAvailability() {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [blocks, setBlocks] = useState({}); // map: { 'YYYY-MM-DD': [{slot, id}, ...] }
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const monthLabel = useMemo(
    () => cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" }),
    [cursor]
  );
  const grid = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const start = grid[0].date;
  const end = grid[grid.length - 1].date;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/admin/ops/availability?start=${start}&end=${end}`);
      const m = {};
      for (const b of data.blocks || []) {
        if (!m[b.date]) m[b.date] = [];
        m[b.date].push(b);
      }
      setBlocks(m);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
      else toast.error(formatApiError(e.response?.data?.detail) || "Failed to load blocks");
    } finally { setLoading(false); }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const addBlock = async (date, slot, override = false) => {
    setBusy(true);
    try {
      await api.post("/admin/ops/availability", { date, slot, override });
      toast.success(`${slot.toUpperCase()} blocked on ${date}`);
      setSelected(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "existing_bookings") {
        if (window.confirm(`${detail.message}\n\nProceed anyway?`)) {
          await addBlock(date, slot, true);
          return;
        }
      } else {
        toast.error(formatApiError(detail) || "Block failed");
      }
    } finally { setBusy(false); }
  };

  const removeBlock = async (id) => {
    setBusy(true);
    try {
      await api.delete(`/admin/ops/availability/${id}`);
      toast.success("Block removed");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Remove failed");
    } finally { setBusy(false); }
  };

  if (forbidden) {
    return (
      <div className="max-w-md mx-auto px-5 py-16 text-center" data-testid="availability-forbidden">
        <ShieldAlert size={28} className="mx-auto text-[#ed6500] mb-4" strokeWidth={1.6} />
        <div className="font-serif text-2xl mb-2">Not permitted</div>
        <p className="text-sm text-[#515E7B]">This dashboard is restricted to the Link operations account.</p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10" data-testid="admin-availability">
        <header className="pb-5 mb-6 border-b hairline flex items-center justify-between gap-3">
          <div>
            <div className="label-xs text-[#ed6500]">Availability</div>
            <h1 className="font-serif text-2xl sm:text-3xl leading-none mt-1" data-testid="availability-month-label">{monthLabel}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              className="p-2 text-[#515E7B] hover:text-[#ed6500]"
              aria-label="Previous month"
              data-testid="availability-prev-month"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
              className="text-[10px] tracking-widest uppercase text-[#515E7B] hover:text-[#ed6500] px-2"
              data-testid="availability-this-month"
            >
              This month
            </button>
            <button
              onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
              className="p-2 text-[#515E7B] hover:text-[#ed6500]"
              aria-label="Next month"
              data-testid="availability-next-month"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#515E7B]" data-testid="availability-loading">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : (
          <div className="border hairline bg-white" data-testid="availability-calendar">
            <div className="grid grid-cols-7 border-b hairline text-[10px] tracking-widest uppercase text-[#515E7B]">
              {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
                <div key={d} className="p-2 sm:p-3 text-center">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {grid.map((cell) => {
                const dayBlocks = blocks[cell.date] || [];
                const hasAll = dayBlocks.some((b) => b.slot === "all");
                const hasAm  = dayBlocks.some((b) => b.slot === "am");
                const hasPm  = dayBlocks.some((b) => b.slot === "pm");
                const isToday = cell.date === todayIso();
                const isPast = cell.date < todayIso();
                return (
                  <button
                    key={cell.date}
                    onClick={() => setSelected(cell.date)}
                    className={`relative aspect-square min-h-[60px] sm:min-h-[80px] p-2 text-left text-xs border-r border-b hairline transition-colors ${
                      cell.outOfMonth ? "bg-[#F9F7F4] text-[#bcbab2]" : "bg-white hover:bg-[#F9F7F4]"
                    } ${isToday ? "ring-1 ring-inset ring-[#ed6500]" : ""} ${hasAll ? "bg-[#ed6500]/8" : ""}`}
                    data-testid={`availability-cell-${cell.date}`}
                  >
                    <div className={`${isToday ? "text-[#ed6500] font-semibold" : ""}`}>
                      {cell.day}
                    </div>
                    <div className="mt-1 flex flex-wrap gap-0.5 text-[8px] tracking-widest uppercase font-semibold">
                      {hasAll && <span className="text-[#ed6500]">Closed</span>}
                      {!hasAll && hasAm && <span className="text-[#ed6500]">AM</span>}
                      {!hasAll && hasPm && <span className="text-[#ed6500]">PM</span>}
                    </div>
                    {isPast && !cell.outOfMonth && (
                      <div className="absolute inset-0 bg-white/40 pointer-events-none" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {selected && (
          <DayBlockSheet
            date={selected}
            blocks={blocks[selected] || []}
            busy={busy}
            onClose={() => setSelected(null)}
            onAdd={(slot) => addBlock(selected, slot)}
            onRemove={(id) => removeBlock(id)}
          />
        )}
      </div>
    </>
  );
}

function DayBlockSheet({ date, blocks, busy, onClose, onAdd, onRemove }) {
  const dt = new Date(date + "T00:00:00");
  const label = dt.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  const has = (s) => blocks.some((b) => b.slot === s);
  const findId = (s) => blocks.find((b) => b.slot === s)?.id;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="availability-sheet">
      <div className="bg-[#F9F7F4] w-full sm:max-w-md sm:rounded-none border-t hairline sm:border hairline" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 sm:p-5 border-b hairline">
          <div>
            <div className="label-xs text-[#ed6500]">Block availability</div>
            <div className="font-serif text-xl mt-1" data-testid="availability-sheet-date">{label}</div>
          </div>
          <button onClick={onClose} className="p-2 text-[#515E7B] hover:text-[#0A1128]" aria-label="Close" data-testid="availability-sheet-close">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 sm:p-5 space-y-3">
          {["all","am","pm"].map((slot) => (
            <div key={slot} className="flex items-center justify-between bg-white border hairline p-3 sm:p-4">
              <div>
                <div className="font-serif text-base">
                  {slot === "all" ? "Whole day" : slot === "am" ? "AM (before noon)" : "PM (noon onward)"}
                </div>
                <div className="text-[11px] text-[#515E7B] mt-0.5">
                  {has(slot) ? "Currently blocked" : "Currently available"}
                </div>
              </div>
              {has(slot) ? (
                <button
                  disabled={busy}
                  onClick={() => onRemove(findId(slot))}
                  className="text-[10px] tracking-widest uppercase font-semibold text-[#5c97f7] hover:text-[#0A1128] disabled:opacity-50"
                  data-testid={`availability-unblock-${slot}`}
                >
                  Unblock
                </button>
              ) : (
                <button
                  disabled={busy}
                  onClick={() => onAdd(slot)}
                  className="text-[10px] tracking-widest uppercase font-semibold text-[#ed6500] hover:text-[#0A1128] disabled:opacity-50"
                  data-testid={`availability-block-${slot}`}
                >
                  Block
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function buildMonthGrid(cursor) {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const first = new Date(y, m, 1);
  const startWeekday = first.getDay(); // 0=Sun
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  // leading days from prev month
  for (let i = startWeekday - 1; i >= 0; i--) {
    const d = new Date(y, m, -i);
    cells.push({ date: iso(d), day: d.getDate(), outOfMonth: true });
  }
  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(y, m, i);
    cells.push({ date: iso(d), day: i, outOfMonth: false });
  }
  while (cells.length % 7 !== 0 || cells.length < 35) {
    const last = cells[cells.length - 1];
    const d = new Date(last.date + "T00:00:00");
    d.setDate(d.getDate() + 1);
    cells.push({ date: iso(d), day: d.getDate(), outOfMonth: d.getMonth() !== m });
    if (cells.length > 42) break;
  }
  return cells;
}
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayIso = () => iso(new Date());
